import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ExclamationCircleOutlined, EyeOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { Table, Row, Col, Breadcrumb, Typography, Modal, Tooltip, Tabs, Form, Select, Button, notification } from 'antd';
import {
  intialPaginationState,
  filter,
  unexpectedError,
  showingPageTotalRange,
  formItemLayout,
  tailFormItemLayout,
  PictureCell,
} from '../../common_functions';
import {
  GrayBasicIcon,
  GrayGlossmeterIcon,
  GrayPrintIcon,
  GreenPrintIcon,
  GrayEditIcon,
  GrayPhysicalIcon,
  GrayXrfIcon,
  GrayMicrometerIcon,
  GrayPhotospectrometerIcon,
  GrayImageIcon,
  GrayMountIcon,
  GreenPhysicalIcon,
  GreenXrfIcon,
  GreenMicrometerIcon,
  GreenGlossmeterIcon,
  GreenPhotospectrometerIcon,
  GreenImageIcon,
  GreenMountIcon,
  GreenBasicIcon,
  GreenEditIcon,
} from '../icons';
import PrintAdvanceSearchModal from '../modals/PrintAdvanceSearchModal';

const PrintObjects = props => {
  const { getItems, prints_negatives_obj, roleType, collections_permissions } = props;
  const formRef = useRef(null);
  const childFormRef = useRef(null);
  const { section, id } = useParams();
  const [objects, setObjects] = useState([]);
  const [newObjects, setNewObjects] = useState([]);
  const [pagination, setPagination] = useState(intialPaginationState);
  const [sorter, setSorter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [TabActiveKey, setTabActiveKey] = useState('1');
  const [locations, setLocations] = useState([]);
  const [collections, setCollections] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const defaultFilter = {
    object_type: {
      _eq: 'print',
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      await getCollections();
      await getLocations();
      const stickyFilter = localStorage.getItem('print_sticky_filter');
      const currentPage = localStorage.getItem('sticky_current_pagination');
      const currentTab = localStorage.getItem('sticky_current_tab');
      if (stickyFilter) {
        formRef.current.setFieldsValue(JSON.parse(stickyFilter));
      }
      if (currentTab) {
        setTabActiveKey(currentTab);
      }
      if (currentPage) {
        await handleTabChange(currentTab);
      } else {
        await getObjectsData(pagination, {}, '', currentTab ? currentTab : 1);
      }
    };
    fetchData();
  }, []);

  // useEffect(() => {
  //   setDisabled(true);
  //   if (formRef?.current?.getFieldValue('locations') || formRef?.current?.getFieldValue('collections')) {
  //     setDisabled(false);
  //   }
  // }, [formRef?.current?.getFieldValue('locations'), formRef?.current?.getFieldValue('collections')]);

  const getLocations = async (collectionID = null) => {
    const filters = collectionID ? { filters: { collections: { _eq: collectionID } } } : {};
    let { data: locations_data } = await getItems('location', {
      fields: ['id', 'name'],
      sort: '-id',
      limit: -1,
      ...filters,
    });
    setLocations(locations_data);
  };

  const getCollections = async () => {
    const { data: collection_data } = await getItems('collections', {
      fields: ['id', 'name'],
      sort: '-id',
      limit: -1,
      filters: {
        associated_to: {
          _in: ['print', 'both'],
        },
      },
    });
    setCollections(collection_data);
  };

  const resetStateAndStorage = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setDisabled(false);
    localStorage.removeItem('sticky_current_pagination');
    setNewObjects([]);
    setObjects([]);
    setPagination(intialPaginationState);
  };
  const handleCommonFinish = async (values, searchType = 'basic_search') => {
    resetStateAndStorage();
    await getObjectsData(intialPaginationState, {}, '', TabActiveKey, searchType);
    localStorage.setItem(
      'print_sticky_filter',
      JSON.stringify({
        collections: values.collections,
        locations: values.locations,
      })
    );
    if (searchType === 'advanced_search') {
      handleClose();
    }
  };

  const onFinish = values => {
    if (!values?.collections && !values?.locations) {
      notification.error({
        message: 'Both Collection and Location fields are empty. Please fill out at least one to perform the search.',
        duration: 2,
      });
    } else {
      return handleCommonFinish(values);
    }
  };
  const onFinishChild = values => {
    if (!values?.acquisition && !values?.object_name && !values?.portfolio_add_number) {
      notification.error({
        message: 'All fields are empty. Please fill out at least one to perform the search.',
        duration: 2,
      });
    } else {
      setFormSubmitted(true);
      return handleCommonFinish(values, 'advanced_search');
    }
  };

  const getObjectsData = async (pagination, filters = {}, sorter = '', tabKey = 1, searchType = 'basic_search') => {
    setLoading(true);
    try {
      const locationID = formRef?.current?.getFieldValue('locations');
      const collectionID = formRef?.current?.getFieldValue('collections');
      const acquisitionID = childFormRef?.current?.getFieldValue('acquisition');
      const portfolioAddNumber = childFormRef?.current?.getFieldValue('portfolio_add_number');
      const commonTitle = childFormRef?.current?.getFieldValue('object_name');
      const conditions = [];
      let checkData = 0;
      let statusFilter = {};
      let printFilters = {};

      if (roleType != 'admin' && prints_negatives_obj?.length > 0) {
        filters['collections'] = { _in: collections_permissions.view };
      }

      if (collectionID) {
        filters['collections'] = { _eq: collectionID };
      }
      if (locationID) {
        filters['location'] = { _eq: locationID };
      }
      if (formSubmitted || searchType === 'advanced_search') {
        if (filters?.acquisition) {
          conditions.push({ acquisition: { _icontains: acquisitionID } });
          conditions.push({ acquisition: filters.acquisition });
        } else if (acquisitionID) {
          filters.acquisition = { _icontains: acquisitionID };
        }
        if (portfolioAddNumber) {
          conditions.push({ print: { portfolio_add_number: { _eq: portfolioAddNumber } } });
        }

        if (commonTitle) {
          conditions.push({ print: { object_name: { _icontains: commonTitle } } });
        }

        if (conditions.length > 0) {
          printFilters._and = conditions;
        }
      }

      let condition = props.roleType == 'admin' || (!!props.tabs_permissions.prints?.view && props.prints_negatives_obj.length > 0);

      // if user view the list from the collection table
      if (section === 'list' && id) {
        filters = {
          _and: [
            {
              object_type: {
                _eq: 'print',
              },
            },
            {
              collections: {
                _eq: id,
              },
            },
          ],
        };
      }

      const subfilterMapping = [
        'object',
        'print',
        'photo_spectro_meter',
        'phy_char',
        'xrf',
        'micrometer',
        'print_mount_conditions',
        'basic_photos',
        'image_description',
        'glossmeter',
      ];
      if (tabKey == 1) {
        statusFilter['_and'] = [
          ...subfilterMapping.map(field => ({
            basic_print_report: { [field]: { _eq: tabKey } },
          })),
        ];
      } else {
        statusFilter['_or'] = [
          ...subfilterMapping.map(field => ({
            basic_print_report: { [field]: { _eq: tabKey } },
          })),
        ];
      }
      let updatedFilter = {};

      updatedFilter['_and'] = [{ ...defaultFilter }, { ...filters }, { ...printFilters }, { ...statusFilter }];

      if (!!condition) {
        const response = await props.getItems('collection_obj', {
          fields: [
            'id,title,provenance,acquisition,collections.name,collections.id,print.thumbnail,print.object_name,print.micrometer,print.glossmeter,print.photo_spectro_meter,basic_print_report.*,location.name',
          ],
          filters: updatedFilter,
          sort: sorter && sorter.field ? `${sorter.order === 'ascend' ? '' : '-'}${sorter.field}` : '-id',
          offset: pagination.pageSize * (pagination.current - 1),
          limit: pagination.pageSize,
          meta: 'filter_count',
        });
        pagination.total = response.meta.filter_count;
        setPagination(pagination);
        if (tabKey == 1) {
          setNewObjects([]);
          setObjects(response.data);
        }
        if (tabKey == 0) {
          setObjects([]);
          setNewObjects(response.data);
        }
        // setLoading(false);
        checkData = response.data.length;
      }
      /**Show Popup if collection prints is not created **/
      if (section === 'list' && id && !checkData && (!!props.tabs_permissions.prints?.create || props.roleType == 'admin')) {
        Modal.confirm({
          icon: <ExclamationCircleOutlined style={{ color: '#1890ff' }} />,
          title: 'Information',
          content: 'Print is not created yet',
          okText: 'Create now',
          cancelText: 'Not now',
          centered: true,
          closable: true,
          keyboard: true,
          onOk() {
            props.history.push(`/print-objects/collection/${id}`);
          },
          onCancel() {
            console.log('Cancel');
          },
        });
      }
    } catch (error) {
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'id' ? sortedInfo.order : null,
      ellipsis: true,
      render: id => {
        return <>{!!id && id}</>;
      },
    },
    {
      title: 'Collection',
      dataIndex: 'collections.name',
      key: 'collections.name',
      sorter: true,
      width: 200,
      sortOrder: sortedInfo.columnKey === 'collections.name' ? sortedInfo.order : null,
      ellipsis: true,
      render: (text, record) => {
        return record?.collections?.name;
      },
    },
    {
      title: 'Acquisition',
      dataIndex: 'acquisition',
      key: 'acquisition',
      sorter: true,
      width: 150,
      sortOrder: sortedInfo.columnKey === 'acquisition' ? sortedInfo.order : null,
      filteredValue: filteredInfo.acquisition || null,
      ellipsis: true,
      ...filter('acquisition'),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      width: 200,
      ...filter('title'),
      sortOrder: sortedInfo.columnKey === 'title' ? sortedInfo.order : null,
      filteredValue: filteredInfo.title || null,
      ellipsis: true,
    },
    {
      title: 'Provenance',
      dataIndex: 'provenance',
      key: 'provenance',
      sorter: true,
      width: 200,
      ...filter('provenance'),
      sortOrder: sortedInfo.columnKey === 'provenance' ? sortedInfo.order : null,
      filteredValue: filteredInfo.provenance || null,
      ellipsis: true,
    },
    {
      title: 'Print Common Title',
      dataIndex: 'print.object_name',
      key: 'print.object_name',
      sorter: true,
      width: 220,
      ...filter('print.object_name'),
      sortOrder: sortedInfo.columnKey === 'print.object_name' ? sortedInfo.order : null,
      filteredValue: filteredInfo?.['print.object_name'] || null,
      ellipsis: true,
      render: (text, record) => {
        return <>{record?.print?.object_name ?? 'Not Created'}</>;
      },
    },

    {
      title: 'Print Thumbnail',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      align: 'center',
      width: 200,
      render: (text, record) =>
        record?.print?.thumbnail ? (
          <PictureCell dataIndexValue={record.print.thumbnail} getFile={props.getFile} width={50} />
        ) : (
          <span>-</span>
        ),
    },
    {
      title: 'Location',
      dataIndex: 'location.name',
      key: 'location.name',
      sorter: true,
      width: 200,
      // ...filter('location'),
      sortOrder: sortedInfo.columnKey === 'location.name' ? sortedInfo.order : null,
      filteredValue: filteredInfo.location || null,
      ellipsis: true,

      render: (text, record) => {
        return record?.location?.name;
      },
    },
    {
      title: 'Actions',
      key: 'action',
      fixed: 'right',
      width: 200,
      render: (text, record) => (
        <span>
          <div className="actionbtngroup">
            <Link to={'/print-objects/edit/' + record.id}>
              <Tooltip placement="top" title={props.roleType === 'admin' || props?.tabs_permissions?.prints?.edit ? 'Edit' : 'View'}>
                <button className={record.basic_print_report[0].object == 1 ? 'print-icon' : 'complete-btn'}>
                  {props.roleType === 'admin' || props?.tabs_permissions?.prints?.edit ? (
                    record.basic_print_report[0].object == 1 ? (
                      <GrayEditIcon />
                    ) : (
                      <GreenEditIcon />
                    )
                  ) : (
                    <EyeOutlined />
                  )}
                </button>
              </Tooltip>
            </Link>
            <Tooltip placement="top" title={!!record.print ? 'Edit Print' : 'Create Print'}>
              <button className="print-icon" onClick={() => updatePrintObject(record.id, '2')}>
                {record.basic_print_report[0].print == 1 ? <GrayPrintIcon /> : <GreenPrintIcon />}
              </button>
            </Tooltip>

            <Tooltip placement="top" title="Physical Characteristics">
              <button className="print-icon" onClick={() => updatePrintObject(record.id, '3')}>
                {record.basic_print_report[0].phy_char == 1 ? <GrayPhysicalIcon /> : <GreenPhysicalIcon />}
              </button>
            </Tooltip>

            <Tooltip placement="top" title="XRF">
              <button className="print-icon" onClick={() => updatePrintObject(record.id, 'xrf')}>
                {record.basic_print_report[0].xrf == 1 ? <GrayXrfIcon /> : <GreenXrfIcon />}
              </button>
            </Tooltip>

            <Tooltip placement="top" title={!!(record.print && record.print.micrometer.length) ? 'Edit Micrometer' : 'Add Micrometer'}>
              <button className="print-icon" onClick={() => updatePrintObject(record.id, 'micrometer')}>
                {record.basic_print_report[0].micrometer == 1 ? <GrayMicrometerIcon /> : <GreenMicrometerIcon />}
              </button>
            </Tooltip>
            <Tooltip placement="top" title={!!(record.print && record.print.glossmeter.length) ? 'Edit Glossmeter' : 'Add Glossmeter'}>
              <button className="print-icon" onClick={() => updatePrintObject(record.id, 'glossmeter')}>
                {record.basic_print_report[0].glossmeter == 1 ? <GrayGlossmeterIcon /> : <GreenGlossmeterIcon />}
              </button>
            </Tooltip>
            <Tooltip
              placement="top"
              title={!!(record.print && record.print.photo_spectro_meter.length) ? 'Edit Photospectrometer' : 'Add Photospectrometer'}
            >
              <button className="print-icon" onClick={() => updatePrintObject(record.id, 'photospectrometer')}>
                {record.basic_print_report[0].photo_spectro_meter == 1 ? <GrayPhotospectrometerIcon /> : <GreenPhotospectrometerIcon />}
              </button>
            </Tooltip>
            <Tooltip placement="top" title="Image Description">
              <button className="print-icon" onClick={() => updatePrintObject(record.id, 'image_desc')}>
                {record.basic_print_report[0].image_description == 1 ? <GrayImageIcon /> : <GreenImageIcon />}
              </button>
            </Tooltip>
            <Tooltip placement="top" title="Mount Characteristics">
              <button className="print-icon" onClick={() => updatePrintObject(record.id, 'mount_condition')}>
                {record.basic_print_report[0].print_mount_conditions == 1 ? <GrayMountIcon /> : <GreenMountIcon />}
              </button>
            </Tooltip>
            <Tooltip placement="top" title="Basic Photos">
              <button className="print-icon" onClick={() => updatePrintObject(record.id, 'basic_photo')}>
                {record.basic_print_report[0].basic_photos == 1 ? <GrayBasicIcon /> : <GreenBasicIcon />}
              </button>
            </Tooltip>
            {/* <Tooltip placement="top" title="Paper Group">
              <button className="print-icon" onClick={() => updatePrintObject(record.id, 'paper_group')}>
                <GreenBasicIcon />
              </button>
            </Tooltip> */}
            {props.deleteRecord(
              props,
              record.id,
              'collection_obj',
              'print object and its associated records?',
              TabActiveKey == 1 ? objects : newObjects,
              TabActiveKey == 1 ? setObjects : setNewObjects,
              props?.tabs_permissions?.prints?.delete
            )}
          </div>
        </span>
      ),
    },
  ];
  let updatePrintObject = (id, tab) => {
    localStorage.setItem('tabActive', tab);
    props.history.push('/print-objects/edit/' + id);
  };
  let handleTableChange = async (pagination, filters, sorter, tab = '') => {
    sorter = sorter?.order == null ? '' : sorter;
    setPagination(pagination);
    setSorter(sorter);
    setLoading(true);
    let filtersitems = {};
    for (let key in filters) {
      if (filters.hasOwnProperty(key)) {
        let val = filters[key];

        if (val) {
          if (key === 'collections.name') {
            filtersitems['collections'] = { name: { _icontains: val[0] } };
          } else if (key === 'print.object_name') {
            filtersitems['print'] = { object_name: { _icontains: val[0] } };
          } else if (key === 'location.name') {
            filtersitems['location'] = { name: { _icontains: val[0] } };
          } else {
            filtersitems[key] = { _icontains: val[0] };
          }
        }
      }
    }
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    await getObjectsData(pagination, filtersitems, sorter, TabActiveKey);
    localStorage.setItem('sticky_current_pagination', pagination.current);
    localStorage.setItem('sticky_current_tab', TabActiveKey);
    if (TabActiveKey == 1) {
      setNewObjects([]);
    }
    if (TabActiveKey == 0) {
      setObjects([]);
    }
  };

  let handleTabChange = async key => {
    localStorage.setItem('sticky_current_tab', key);
    setTabActiveKey(key);
    const currentPage = localStorage.getItem('sticky_current_pagination');
    if (currentPage) {
      intialPaginationState.current = Number(currentPage);
    }
    await getObjectsData(intialPaginationState, {}, '', key);
    if (key == 1) {
      setNewObjects([]);
    }
    if (key == 0) {
      setObjects([]);
    }
  };
  const resetFilters = async (isChild = false) => {
    childFormRef.current?.resetFields();
    if (isChild) {
      handleClose();
      setDisabled(disabled);
    } else {
      setNewObjects([]);
      setObjects([]);
      setDisabled(true);

      localStorage.removeItem('print_sticky_filter');
      localStorage.removeItem('sticky_current_pagination');
      localStorage.removeItem('sticky_current_tab');

      const collections = formRef.current?.getFieldValue('collections');
      if (collections) {
        await getLocations();
      }
      formRef.current?.resetFields();
    }
    await getObjectsData(intialPaginationState, {}, '', TabActiveKey);
    setFormSubmitted(true);
  };

  // const resetFilters = async (isChild = false) => {
  //   if (!isChild) {
  //     setNewObjects([]);
  //     setObjects([]);
  //     localStorage.removeItem('print_sticky_filter');
  //     localStorage.removeItem('sticky_current_pagination');
  //     localStorage.removeItem('sticky_current_tab');
  //     setDisabled(true);
  //   }
  //   if (isChild) {
  //     childFormRef.current?.resetFields();
  //     handleClose();
  //     setDisabled(disabled);
  //   } else {
  //     formRef.current?.resetFields();
  //     childFormRef.current?.resetFields();
  //     setDisabled(true);
  //     const locationID = formRef?.current?.getFieldValue('locations');
  //   }
  //   if(!isChild && formRef?.current?.getFieldValue('locations')){
  //        await getLocations();
  //   }
  //   await getObjectsData(intialPaginationState, {}, '', TabActiveKey);
  //   setFormSubmitted(true);
  // };

  // Alias for child form reset
  const childResetFiters = async () => resetFilters(true);

  const commonTable = dataSource => {
    return (
      <div className="users-list">
        <Table
          rowKey="id"
          scroll={{
            x: 1500,
          }}
          columns={columns}
          dataSource={dataSource}
          pagination={{ ...pagination, ...showingPageTotalRange }}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
    );
  };

  const handleClose = () => {
    setIsModalVisible(false);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };
  return (
    <Row>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Print Objects" }]} />
      </Col>
      <Col className="white-paper" span={24}>
        <div className="dashboard">
          <Row gutter={24} className="page-header-section report-buttons-row" align="middle">
            <Col xs={24} sm={24} md={6} lg={6}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Print Object List
              </Typography.Title>
            </Col>
            <Col xs={24} sm={24} md={18} lg={18} justify="end" align="right">
              {section === 'list' && id
                ? props.createButton(
                    props.roleType,
                    props.tabs_permissions.prints,
                    `/print-objects/collection/${id}`,
                    'Create Print Object'
                  )
                : props.createButton(props.roleType, props.tabs_permissions.prints, `/print-objects/new`, 'Create Print Object', '')}
            </Col>
            {/* <Col xs={12} justify="end">
              {section === 'list' && id
                ? props.createButton(
                    props.roleType,
                    props.tabs_permissions.prints,
                    `/print-objects/collection/${id}`,
                    'Create Print Object'
                  )
                : props.createButton(props.roleType, props.tabs_permissions.prints, `/print-objects/new`, 'Create Print Object')}
            </Col> */}
          </Row>
          <Form ref={formRef} layout="vertical" onFinish={onFinish} {...formItemLayout} initialValues={{ layout: tailFormItemLayout }}>
            <Row gutter={24}>
              <Col xs={24} sm={24} md={6} lg={6}>
                <Form.Item name="collections" label="Collection">
                  <Select
                    showSearch
                    placeholder="Select collection"
                    optionFilterProp="children"
                    allowClear
                    filterOption={(input, option) => option.children.includes(input)}
                    onChange={getLocations}
                  >
                    {collections.map(col => {
                      return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={6} lg={6}>
                <Form.Item name="locations" label="Locations">
                  <Select
                    placeholder="Select Location"
                    optionFilterProp="children"
                    showSearch
                    allowClear
                    filterOption={(input, option) => option.children.includes(input)}
                  >
                    {locations.map(col => {
                      return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>

              <Form.Item>
                <Button
                  type="primary"
                  size="small"
                  htmlType="submit"
                  className="filter-form-button report-button-margin-right"
                  icon={<SearchOutlined />}
                >
                  Search
                </Button>
                <Button
                  className="filter-form-button report-button-margin-left"
                  icon={<SearchOutlined />}
                  type="primary"
                  size="small"
                  onClick={showModal}
                >
                  Advanced Search
                </Button>
                <Button
                  size="small"
                  danger
                  className="filter-form-button report-button-margin-left"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    resetFilters();
                  }}
                  disabled={disabled}
                >
                  Reset all filters
                </Button>
              </Form.Item>
            </Row>
          </Form>
          <hr />
          <Tabs defaultActiveKey={TabActiveKey} activeKey={TabActiveKey} onChange={handleTabChange} items={[{
  label: "Complete",
  key: "1",
  children: commonTable(objects)
}, {
  label: "Incomplete",
  key: "0",
  children: commonTable(newObjects)
}]} />
        </div>
        <PrintAdvanceSearchModal
          visible={isModalVisible}
          onClose={handleClose}
          onFinish={onFinishChild}
          formRef={childFormRef}
          resetFilters={childResetFiters}
        />
      </Col>
    </Row>
  );
};
export default PrintObjects;
